import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Fragment, useMemo, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button/new';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { B300 } from '@atlaskit/theme/colors';
import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { NLPSearchErrorState } from '../../../../ai-answer-dialog/types';
import { AiBrand } from '../../../../constants';
import { ATLASSIAN_ACCEPTABLE_USE_POLICY_LINK, ATLASSIAN_AI_TROUBLESHOOT_LINK, ATLASSIAN_HIPAA_COMPLICANCE_LINK } from '../../common/constants';
import { DefinitionUpdateConfirmationFlag } from '../../curated/edit-definition-modal/definition-update-confirmation-flag';
import { EditDefinitionModal } from '../../curated/edit-definition-modal/edit-definition-modal';
import { messages } from './messages';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
var InfoLink = styled.a({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
  color: "".concat("var(--ds-text-accent-blue, ".concat(B300, ")"), " !important"),
  textDecoration: 'underline'
});
var ErrorPaddingStyles = xcss({
  paddingBottom: 'space.100'
});
var ErrorMessageStyles = xcss({
  font: "var(--ds-font-body, normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  color: 'color.text'
});
export var DefinitionError = function DefinitionError() {
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    brand = _useAIAnswerContext.brand,
    errorState = _useAIAnswerContext.commonAttributes.errorState,
    readingAidsOptions = _useAIAnswerContext.readingAidsOptions;
  var _ref = readingAidsOptions || {},
    definitionCurationOptions = _ref.definitionCurationOptions;
  var createDefinitionEnabled = !!definitionCurationOptions;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showEditDefinitionModal = _useState2[0],
    setShowEditDefinitionModal = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    displaySuccessFlag = _useState4[0],
    setDisplaySuccessFlag = _useState4[1];
  var showAddDefinitionButton = createDefinitionEnabled && (errorState === NLPSearchErrorState.NoAnswer || errorState === NLPSearchErrorState.NoAnswerKeywords || errorState === NLPSearchErrorState.NoAnswerSearchResults || errorState === NLPSearchErrorState.NoAnswerHydration || errorState === NLPSearchErrorState.NoAnswerRelevantContent || errorState === NLPSearchErrorState.Default);
  var errorMessage = useMemo(function () {
    switch (errorState) {
      case NLPSearchErrorState.AcceptableUseViolation:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.definition_no_answer_acceptable_use_error, {
          values: {
            acceptableUseLink: /*#__PURE__*/React.createElement(InfoLink, {
              href: ATLASSIAN_ACCEPTABLE_USE_POLICY_LINK,
              target: '_blank'
            }, formatMessage(messages.definition_no_answer_acceptable_use_link)),
            brand: brand === AiBrand.AI ? 'Atlassian Intelligence' : 'Rovo'
          }
        }));
      case NLPSearchErrorState.NetworkError:
      case NLPSearchErrorState.NoAnswerOpenAIResponseError:
      case NLPSearchErrorState.AIDisabled:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.definition_no_answer_network_error, {
          values: {
            brand: brand === AiBrand.AI ? 'Atlassian Intelligence' : 'Rovo',
            supportLink: /*#__PURE__*/React.createElement(InfoLink, {
              href: ATLASSIAN_AI_TROUBLESHOOT_LINK,
              target: '_blank'
            }, formatMessage(messages.definitions_support_link_text))
          }
        }));
      case NLPSearchErrorState.HIPAAError:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.definitions_no_answer_error_state_hipaa, {
          values: {
            supportLink: /*#__PURE__*/React.createElement(InfoLink, {
              href: ATLASSIAN_HIPAA_COMPLICANCE_LINK,
              target: '_blank'
            }, formatMessage(messages.definitions_no_answer_hipaa_compliance_link))
          }
        }));
      case NLPSearchErrorState.NoAnswer:
      case NLPSearchErrorState.NoAnswerKeywords:
      case NLPSearchErrorState.NoAnswerSearchResults:
      case NLPSearchErrorState.NoAnswerHydration:
      case NLPSearchErrorState.NoAnswerRelevantContent:
      case NLPSearchErrorState.Default:
      default:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.definition_no_answer_error, {
          values: {
            brand: brand === AiBrand.AI ? 'Atlassian Intelligence' : 'Rovo'
          }
        }));
    }
  }, [errorState, formatMessage, brand]);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Box, {
    xcss: ErrorPaddingStyles
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: ErrorMessageStyles
  }, errorMessage), showAddDefinitionButton && /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "end",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Button, {
    appearance: "default",
    onClick: function onClick() {
      return setShowEditDefinitionModal(true);
    }
  }, formatMessage(messages.definition_no_answer_add_definition_button)))), /*#__PURE__*/React.createElement(ModalTransition, null, showEditDefinitionModal && showAddDefinitionButton && /*#__PURE__*/React.createElement(EditDefinitionModal, {
    curatedDefinitionScopeConfig: definitionCurationOptions.curatedDefinitionScopeConfig,
    closeEditModal: function closeEditModal() {
      return setShowEditDefinitionModal(false);
    },
    isCreateDefinition: true,
    onSaveEditModal: function onSaveEditModal() {
      return setDisplaySuccessFlag(true);
    }
  })), displaySuccessFlag && /*#__PURE__*/React.createElement(DefinitionUpdateConfirmationFlag, {
    onDismissed: function onDismissed() {
      return setDisplaySuccessFlag(false);
    }
  }));
};